import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Statistic,
  Alert,
  Typography,
  Popconfirm
} from 'antd';
import {
  KeyOutlined,
  PlusOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CopyOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EditOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { Text, Paragraph } = Typography;

const MerchantAPIKeys = () => {
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Get merchant info from localStorage
  const [merchantData, setMerchantData] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [userId, setUserId] = useState(null);

  // Safe JSON parsing helper function
  const safeJSONParse = (value) => {
    if (!value || value === 'null' || value === null) return [];
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        console.warn('Failed to parse JSON:', value, e);
        return [];
      }
    }
    return Array.isArray(value) ? value : [];
  };

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('merchant_user');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setMerchantData(parsed);
        setMemberId(parsed.member_id);
      } else {
        // For development/testing - use test member ID
        setMemberId('MBR000007');
      }
    } catch (error) {
      console.error('Error parsing merchant data:', error);
      // Fallback to test member ID
      setMemberId('MBR000007');
    }
  }, []);

  useEffect(() => {
    if (memberId) {
      fetchAPIKeys();
    }
  }, [memberId]);

  const fetchAPIKeys = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }
      const response = await axios.get(`/api/merchant/callbacks/api-keys`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('API Keys Response:', response.data.api_keys);
      setApiKeys(response.data.api_keys);
    } catch (error) {
      message.error('Gagal memuat API keys');
      console.error('Fetch API keys error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingKey(record);
    editForm.setFieldsValue({
      key_name: record.key_name,
      permissions: safeJSONParse(record.permissions)
      // rate_limit_per_minute: record.rate_limit_per_minute // Disabled for now
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }

      await axios.put(`/api/merchant/callbacks/api-keys/${editingKey.id}`, values, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      message.success('API key berhasil diperbarui');
      setEditModalVisible(false);
      setEditingKey(null);
      fetchAPIKeys();
    } catch (error) {
      message.error('Gagal memperbarui API key');
      console.error('Update API key error:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }

      const response = await axios.post('/api/merchant/callbacks/api-keys', values, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      Modal.success({
        title: 'API Key Berhasil Dibuat',
        content: (
          <div>
            <p><strong>API Key:</strong></p>
            <Input.TextArea
              value={response.data.api_key}
              readOnly
              autoSize={{ minRows: 1 }}
              style={{ fontFamily: 'monospace', marginBottom: 8 }}
            />
            <p><strong>Secret Key:</strong></p>
            <Input.TextArea
              value={response.data.secret_key}
              readOnly
              autoSize={{ minRows: 1 }}
              style={{ fontFamily: 'monospace', marginBottom: 8 }}
            />
            <Alert
              message="Simpan Secret Key dengan aman!"
              description="Secret Key ini akan disimpan di database dan dapat dilihat kembali di tabel API Keys. Namun tetap simpan di tempat yang aman."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        ),
        width: 600
      });

      setModalVisible(false);
      fetchAPIKeys();
    } catch (error) {
      message.error('Gagal membuat API key');
      console.error('Create API key error:', error);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }
      await axios.put(`/api/merchant/callbacks/api-keys/${id}/deactivate`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      message.success('API key berhasil dinonaktifkan');
      fetchAPIKeys();
    } catch (error) {
      message.error('Gagal menonaktifkan API key');
      console.error('Deactivate API key error:', error);
    }
  };

  const copyToClipboard = (text, type = 'text') => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'api_key') {
        message.success('API Key berhasil disalin! 📋');
      } else if (type === 'secret_key') {
        message.success('Secret Key berhasil disalin! 🔐');
      } else if (type === 'merchant_code') {
        message.success('Merchant Code berhasil disalin! 🏪');
      } else {
        message.success('Berhasil disalin! 📋');
      }
    }).catch(err => {
      console.error('Gagal menyalin:', err);
      message.error('Gagal menyalin!');
    });
  };

  const toggleSecretVisibility = (id) => {
    setShowSecrets(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getPermissionColor = (permission) => {
    const colors = {
      'create_payment': 'green',
      'read_transactions': 'blue',
      'webhook_access': 'orange',
      'read_balance': 'purple',
      'create_refund': 'red'
    };
    return colors[permission] || 'default';
  };

  const getPermissionText = (permission) => {
    const texts = {
      'create_payment': 'Create Payment',
      'read_transactions': 'Read Transactions',
      'webhook_access': 'Webhook Access',
      'read_balance': 'Read Balance',
      'create_refund': 'Create Refund'
    };
    return texts[permission] || permission;
  };

  const columns = [
    {
      title: 'Key Name',
      dataIndex: 'key_name',
      key: 'key_name',
      width: 150,
    },
    {
      title: 'Merchant Code',
      dataIndex: 'user_id',
      key: 'merchant_code',
      width: 200,
      render: (userId) => {
        const merchantCode = `MERCHANT-${userId}`;
        const displayCode = merchantCode.length > 15
          ? `${merchantCode.substring(0, 15)}...`
          : merchantCode;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text code style={{ flex: 1, color: '#1890ff', fontSize: '12px' }}>{displayCode}</Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(merchantCode, 'merchant_code')}
                title="Copy full Merchant Code"
              />
            </div>
            <Text type="secondary" style={{ fontSize: '10px' }}>
              Partner ID untuk Full Auth
            </Text>
          </div>
        );
      },
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      width: 200,
      render: (key) => {
        const displayKey = key.length > 15
          ? `${key.substring(0, 15)}...`
          : key;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text code style={{ flex: 1, fontSize: '12px' }}>{displayKey}</Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(key, 'api_key')}
              title="Copy full API Key"
            />
          </div>
        );
      },
    },
    {
      title: 'Secret Key',
      dataIndex: 'secret_key',
      key: 'secret_key',
      width: 200,
      render: (key) => key ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text code style={{ flex: 1, fontSize: '12px' }}>
              {key.length > 15 ? `${key.substring(0, 15)}...` : key}
            </Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(key, 'secret_key')}
              title="Copy full Secret Key"
            />
          </div>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            🔐 Keep this secret!
          </Text>
        </div>
      ) : (
        <Tag color="orange">Belum ada</Tag>
      ),
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 200,
      render: (permissions) => {
        const permArray = safeJSONParse(permissions);
        return (
          <Space wrap>
            {permArray.map(perm => (
              <Tag key={perm} color={getPermissionColor(perm)} style={{ fontSize: '11px' }}>
                {getPermissionText(perm)}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    // Rate Limit column - Disabled for now
    // {
    //   title: 'Rate Limit',
    //   dataIndex: 'rate_limit_per_minute',
    //   key: 'rate_limit_per_minute',
    //   width: 100,
    //   render: (limit) => `${limit}/min`,
    // },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString('id-ID') : 'Never',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('id-ID'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          {record.is_active && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              title="Edit API Key"
            />
          )}
          {record.is_active && (
            <Popconfirm
              title="Nonaktifkan API key ini?"
              onConfirm={() => handleDeactivate(record.id)}
              okText="Ya"
              cancelText="Tidak"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const activeKeys = apiKeys.filter(key => key.is_active);

  if (!memberId) {
    return (
      <div>
        <Card loading={true}>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h3>Loading Merchant Data...</h3>
            <p>Sedang memuat data merchant...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>API Keys Management</h1>
        <p style={{ color: '#666' }}>Kelola API keys untuk mengakses Arendo API</p>
      </div>

      {/* Alert Info */}
      <Alert
        message="Informasi Merchant Code"
        description={
          <div>
            <p><strong>Merchant Code (Partner ID)</strong> digunakan untuk autentikasi Full Auth (HMAC-SHA512) di SNAP BI API:</p>
            <ul>
              <li>Gunakan Merchant Code ini di header <Text code>X-PARTNER-ID</Text></li>
              <li>Required headers untuk Full Auth: <Text code>X-TIMESTAMP</Text>, <Text code>X-EXTERNAL-ID</Text>, <Text code>X-PARTNER-ID</Text>, <Text code>X-SIGNATURE</Text></li>
              <li>Untuk Simple Auth, hanya perlu <Text code>X-API-Key</Text></li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Alert
        message="Keamanan API Key"
        description="API Key digunakan untuk autentikasi ke Arendo API. Jangan pernah membagikan API Secret Anda kepada pihak lain atau menyimpannya di client-side code."
        type="warning"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total API Keys"
              value={apiKeys.length}
              prefix={<KeyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Keys"
              value={activeKeys.length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Inactive Keys"
              value={apiKeys.length - activeKeys.length}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Last Activity"
              value={apiKeys.length > 0 ?
                (() => {
                  // Filter out null/undefined/invalid dates
                  const validDates = apiKeys
                    .map(k => {
                      if (!k.last_used_at || k.last_used_at === null || k.last_used_at === undefined) {
                        return null;
                      }
                      const date = new Date(k.last_used_at);
                      // Check if date is valid (not invalid or 1970)
                      return (date && date.getFullYear() > 1970) ? date : null;
                    })
                    .filter(date => date !== null);

                  return validDates.length > 0
                    ? new Date(Math.max(...validDates.map(d => d.getTime()))).toLocaleDateString('id-ID')
                    : 'Never';
                })()
                : 'Never'
              }
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <h3>Daftar API Keys</h3>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Buat API Key Baru
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchAPIKeys}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={apiKeys}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} API keys`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Create Modal */}
      <Modal
        title="Buat API Key Baru"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Key Name"
            name="key_name"
            rules={[{ required: true, message: 'Key name wajib diisi' }]}
            help="Nama untuk mengidentifikasi API key ini"
          >
            <Input placeholder="e.g., Production API, Development API" />
          </Form.Item>

          <Form.Item
            label="Permissions"
            name="permissions"
            rules={[{ required: true, message: 'Permission wajib dipilih' }]}
            help="Pilih permission yang diperlukan untuk API key ini"
          >
            <Select
              mode="multiple"
              placeholder="Pilih permissions"
              allowClear
            >
              <Option value="create_payment">Create Payment</Option>
              <Option value="read_transactions">Read Transactions</Option>
              <Option value="webhook_access">Webhook Access</Option>
              <Option value="read_balance">Read Balance</Option>
              <Option value="create_refund">Create Refund</Option>
            </Select>
          </Form.Item>

          
          {/* Rate Limit Feature - Disabled for now
          <Form.Item
            label="Rate Limit (requests per minute)"
            name="rate_limit_per_minute"
            help="Jumlah maksimal request per menit"
            initialValue={100}
          >
            <Select>
              <Option value={10}>10 requests/minute</Option>
              <Option value={50}>50 requests/minute</Option>
              <Option value={100}>100 requests/minute</Option>
              <Option value={500}>500 requests/minute</Option>
              <Option value={1000}>1000 requests/minute</Option>
            </Select>
          </Form.Item>
          */}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                Buat API Key
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit API Key"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingKey(null);
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="Keamanan API Key"
          description="API Key tidak dapat diubah untuk alasan keamanan. Anda hanya dapat mengubah nama dan permissions."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            label="API Key"
            help="API Key tidak dapat diubah"
          >
            <Input
              value={editingKey?.api_key}
              readOnly
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            label="Key Name"
            name="key_name"
            rules={[{ required: true, message: 'Key name wajib diisi' }]}
            help="Nama untuk mengidentifikasi API key ini"
          >
            <Input placeholder="e.g., Production API, Development API" />
          </Form.Item>

          <Form.Item
            label="Permissions"
            name="permissions"
            rules={[{ required: true, message: 'Permission wajib dipilih' }]}
            help="Pilih permission yang diperlukan untuk API key ini"
          >
            <Select
              mode="multiple"
              placeholder="Pilih permissions"
              allowClear
            >
              <Option value="create_payment">Create Payment</Option>
              <Option value="read_transactions">Read Transactions</Option>
              <Option value="webhook_access">Webhook Access</Option>
              <Option value="read_balance">Read Balance</Option>
              <Option value="create_refund">Create Refund</Option>
            </Select>
          </Form.Item>

          
          {/* Rate Limit Feature - Disabled for now
          <Form.Item
            label="Rate Limit (requests per minute)"
            name="rate_limit_per_minute"
            help="Jumlah maksimal request per menit"
            rules={[{ required: true, message: 'Rate limit wajib dipilih' }]}
          >
            <Select>
              <Option value={10}>10 requests/minute</Option>
              <Option value={50}>50 requests/minute</Option>
              <Option value={100}>100 requests/minute</Option>
              <Option value={500}>500 requests/minute</Option>
              <Option value={1000}>1000 requests/minute</Option>
            </Select>
          </Form.Item>
          */}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                setEditingKey(null);
              }}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                Update API Key
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* API Documentation Card - Hidden for now */}
      {/* <Card title="API Documentation" style={{ marginTop: 24 }}>
        <Paragraph>
          Gunakan API key dan secret untuk mengautentikasi request ke Arendo API:
        </Paragraph>

        <div style={{ marginBottom: 16 }}>
          <Text strong>Base URL:</Text>
          <Text code style={{ marginLeft: 8 }}>https://api.paymentgateway.com/v1</Text>
          <Button
            size="small"
            icon={<CopyOutlined />}
            style={{ marginLeft: 8 }}
            onClick={() => copyToClipboard('https://api.paymentgateway.com/v1')}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong>Authentication Header:</Text>
          <Input.TextArea
            value={`Authorization: Bearer YOUR_API_KEY\nContent-Type: application/json`}
            readOnly
            autoSize={{ minRows: 2 }}
            style={{ fontFamily: 'monospace', marginTop: 8 }}
          />
        </div>

        <div>
          <Text strong>Example Request:</Text>
          <Input.TextArea
            value={`curl -X POST https://api.paymentgateway.com/v1/payments \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 100000,
    "currency": "IDR",
    "payment_method": "qris",
    "customer": {
      "name": "John Doe",
      "email": "john@example.com"
    }
  }'`}
            readOnly
            autoSize={{ minRows: 8 }}
            style={{ fontFamily: 'monospace', marginTop: 8 }}
          />
        </div>
      </Card> */}
    </div>
  );
};

export default MerchantAPIKeys;